import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Snackbar,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Tooltip,
    useTheme,
    alpha,
    Pagination,
    Container,
    IconButton
} from '@mui/material';
import {
    AccountBalance as AccountBalanceIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    Refresh as RefreshIcon,
    Clear as ClearIcon,
    DateRange as DateRangeIcon,
    FilterList as FilterIcon,
    ReceiptLong as LedgerIcon,
    ArrowBack as BackIcon,
    Business as BusinessIcon,
    ConfirmationNumber as VoucherIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory } from 'react-router-dom';

// API import
import { getPaymentLedgerReport_comm } from '../../../api/analytics/analytics';

// Types
interface PaymentLedger {
    id: number;
    walletID: string;
    user_id: string;
    campaign_id: string | null;
    campaign_name: string | null;
    shopID: string;
    drValue: string;
    crValue: string;
    amount: number;
    desp: string;
    dateCreated: string;
    last_updated: string;
}

interface DateRangeFilter {
    startDate: string;
    endDate: string;
}

interface RevenueReportProps {
    config?: any;
    requestId?: string;
}

const RevenueReport: React.FC<RevenueReportProps> = ({ config, requestId }) => {
    const theme = useTheme();
    const history = useHistory();

    // Ledger states
    const [allLedgerEntries, setAllLedgerEntries] = useState<PaymentLedger[]>([]);
    const [filteredEntries, setFilteredEntries] = useState<PaymentLedger[]>([]);

    // Filter states
    const [dateRange, setDateRange] = useState<DateRangeFilter>({
        startDate: '',
        endDate: ''
    });
    const [campaignFilter, setCampaignFilter] = useState<string>('all');
    const [uniqueCampaigns, setUniqueCampaigns] = useState<string[]>([]);

    // UI states
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch ledger data on component mount
    useEffect(() => {
        fetchLedgerData();
    }, []);

    // Apply filters when ledger data, campaign filter, or date range changes
    useEffect(() => {
        applyFilters();
    }, [allLedgerEntries, campaignFilter, dateRange]);

    // Extract unique campaigns for filter dropdown
    useEffect(() => {
        if (allLedgerEntries.length > 0) {
            const campaigns = Array.from(new Set(
                allLedgerEntries
                    .map(entry => entry.campaign_name)
                    .filter((name): name is string => name !== null && name !== 'N/A' && name !== '')
            ));
            setUniqueCampaigns(campaigns);
        }
    }, [allLedgerEntries]);

    const fetchLedgerData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Call the API with config and requestId
            const response = await getPaymentLedgerReport_comm(config, requestId);
            const data = response?.data?.data || response?.data || response;
            const entries = Array.isArray(data) ? data : [];
            setAllLedgerEntries(entries);

            if (entries.length === 0) {
                showNotification('No ledger entries found', 'info');
            } else {
                showNotification(`Loaded ${entries.length} transactions`, 'success');
            }
        } catch (err) {
            console.error('Error fetching ledger data:', err);
            setError('Failed to load ledger data');
            showNotification('Failed to load ledger data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...allLedgerEntries];

        // Apply date range filter
        if (dateRange.startDate && dateRange.endDate) {
            const start = new Date(dateRange.startDate);
            const end = new Date(dateRange.endDate);
            end.setHours(23, 59, 59);

            filtered = filtered.filter(entry => {
                const entryDate = new Date(entry.dateCreated);
                return entryDate >= start && entryDate <= end;
            });
        }

        // Apply campaign filter
        if (campaignFilter !== 'all') {
            filtered = filtered.filter(entry => entry.campaign_name === campaignFilter);
        }

        setFilteredEntries(filtered);
        setPage(1);
    };

    const handleDateChange = (field: keyof DateRangeFilter, value: string) => {
        const newDateRange = { ...dateRange, [field]: value };
        setDateRange(newDateRange);
    };

    const clearDateFilter = () => {
        setDateRange({ startDate: '', endDate: '' });
        setFilteredEntries(allLedgerEntries);
        setPage(1);
    };

    const clearAllFilters = () => {
        setDateRange({ startDate: '', endDate: '' });
        setCampaignFilter('all');
        setFilteredEntries(allLedgerEntries);
        setPage(1);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchLedgerData();
        setRefreshing(false);
        showNotification('Data refreshed successfully', 'success');
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
        } catch {
            return dateString;
        }
    };

    const formatAmount = (amount?: number | string) => {
        if (amount === undefined || amount === null || amount === '') return '₦0.00';
        return `₦${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Calculate summary statistics
    const summary = {
        totalDr: filteredEntries.reduce((sum, entry) => {
            const drValue = parseFloat(entry.drValue) || 0;
            return sum + drValue;
        }, 0),
        totalCr: filteredEntries.reduce((sum, entry) => {
            const crValue = parseFloat(entry.crValue) || 0;
            return sum + crValue;
        }, 0),
        totalAmount: filteredEntries.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0),
        totalTransactions: filteredEntries.length
    };

    // Pagination
    const pageCount = Math.ceil(filteredEntries.length / rowsPerPage);
    const paginatedEntries = filteredEntries.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const hasActiveFilters = dateRange.startDate || dateRange.endDate || campaignFilter !== 'all';

    // Calculate date range text for display
    const getDateRangeText = () => {
        if (dateRange.startDate && dateRange.endDate) {
            return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;
        }
        return 'All time';
    };

    return (
        <IonPage>
            <IonContent>
                <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

                    {/* Header */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            bgcolor: 'background.paper'
                        }}
                    >
                        <Container maxWidth="xl">
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                                <IconButton onClick={() => history.goBack()} edge="start">
                                    <BackIcon />
                                </IconButton>
                                <Avatar
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main
                                    }}
                                >
                                    <LedgerIcon />
                                </Avatar>
                                <Box flex={1}>
                                    <Typography variant="h5" fontWeight={600}>
                                        Revenue Report
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Payment ledger and transaction history
                                    </Typography>
                                </Box>
                                <Tooltip title="Refresh">
                                    <Button
                                        variant="contained"
                                        startIcon={<RefreshIcon />}
                                        onClick={handleRefresh}
                                        disabled={refreshing}
                                        size="small"
                                    >
                                        Refresh
                                    </Button>
                                </Tooltip>
                            </Stack>
                        </Container>
                    </Paper>

                    <Container maxWidth="xl" sx={{ py: 3 }}>

                        {/* Info Card */}
                        <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.info.main, 0.03) }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                        <BusinessIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Report Configuration
                                        </Typography>
                                        <Typography variant="body2">
                                            Request ID: {requestId || 'N/A'} | Date Range: {getDateRangeText()}
                                        </Typography>
                                        {config && (
                                            <Typography variant="caption" color="text.secondary">
                                                Config: {JSON.stringify(config)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Date Range Filter */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <DateRangeIcon color="primary" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Date Range Filter
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        Filter transactions by date
                                    </Typography>
                                    {hasActiveFilters && (
                                        <Chip
                                            label="Active Filters"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            onDelete={clearAllFilters}
                                            deleteIcon={<ClearIcon />}
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Stack>

                                <Grid container spacing={2} alignItems="flex-end">
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="From Date"
                                            value={dateRange.startDate}
                                            onChange={(e) => handleDateChange('startDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="To Date"
                                            value={dateRange.endDate}
                                            onChange={(e) => handleDateChange('endDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={clearDateFilter}
                                            disabled={!dateRange.startDate && !dateRange.endDate}
                                            startIcon={<ClearIcon />}
                                            size="medium"
                                        >
                                            Clear
                                        </Button>
                                    </Grid>
                                </Grid>

                                {/* Campaign Filter */}
                                {uniqueCampaigns.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Filter by Campaign</InputLabel>
                                            <Select
                                                value={campaignFilter}
                                                label="Filter by Campaign"
                                                onChange={(e) => setCampaignFilter(e.target.value)}
                                            >
                                                <MenuItem value="all">All Campaigns</MenuItem>
                                                {uniqueCampaigns.map((campaign) => (
                                                    <MenuItem key={campaign} value={campaign}>
                                                        {campaign}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Loading State */}
                        {loading && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress />
                                <Typography sx={{ mt: 2 }} color="text.secondary">
                                    Loading ledger data...
                                </Typography>
                            </Box>
                        )}

                        {/* Error State */}
                        {error && !loading && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        {/* No Data State */}
                        {!loading && !error && allLedgerEntries.length === 0 && (
                            <Paper sx={{ py: 8, textAlign: 'center' }}>
                                <Stack spacing={2} alignItems="center">
                                    <LedgerIcon sx={{ fontSize: 64, color: theme.palette.grey[300] }} />
                                    <Typography variant="h6" color="text.secondary">
                                        No Transactions Found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        No ledger entries available for this report
                                    </Typography>
                                </Stack>
                            </Paper>
                        )}

                        {/* Filtered No Data State */}
                        {!loading && !error && allLedgerEntries.length > 0 && filteredEntries.length === 0 && (
                            <Paper sx={{ py: 6, textAlign: 'center' }}>
                                <Stack spacing={2} alignItems="center">
                                    <DateRangeIcon sx={{ fontSize: 64, color: theme.palette.grey[300] }} />
                                    <Typography variant="h6" color="text.secondary">
                                        No Matching Transactions
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {dateRange.startDate && dateRange.endDate
                                            ? `No transactions found between ${formatDate(dateRange.startDate)} and ${formatDate(dateRange.endDate)}`
                                            : 'No transactions match your current filters'}
                                    </Typography>
                                    <Button onClick={clearAllFilters} variant="outlined" size="small">
                                        Clear Filters
                                    </Button>
                                </Stack>
                            </Paper>
                        )}

                        {/* Ledger Data Display */}
                        {!loading && !error && filteredEntries.length > 0 && (
                            <>
                                {/* Summary Stats Bar */}
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12}>
                                        <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={2}>
                                                <Stack direction="row" spacing={3} flexWrap="wrap">
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Total Transactions
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={700}>
                                                            {summary.totalTransactions}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Date Range
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {getDateRangeText()}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Unique Campaigns
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {uniqueCampaigns.length}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Chip
                                                    icon={<VoucherIcon />}
                                                    label={`${filteredEntries.length} entries shown`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* Summary Cards */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                                            <CardContent>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                                                        <AccountBalanceIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Total Credit (DR)
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={700} color="success.main">
                                                            {formatAmount(summary.totalDr)}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                                            <CardContent>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                                                        <PaymentIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Total Debit (CR)
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={700} color="error.main">
                                                            {formatAmount(summary.totalCr)}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                                            <CardContent>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                                        <AccountBalanceIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Net Amount
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={700} color="info.main">
                                                            {formatAmount(summary.totalAmount)}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                                            <CardContent>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                                                        <ReceiptIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Total Value
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={700}>
                                                            {formatAmount(Math.abs(summary.totalAmount))}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>

                                {/* Filter Info Bar */}
                                {hasActiveFilters && (
                                    <Paper sx={{ p: 1.5, mb: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" spacing={1}>
                                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                <FilterIcon fontSize="small" color="info" />
                                                <Typography variant="caption" fontWeight={500}>
                                                    Active Filters:
                                                </Typography>
                                                {dateRange.startDate && dateRange.endDate && (
                                                    <Chip
                                                        size="small"
                                                        label={`${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`}
                                                        onDelete={clearDateFilter}
                                                        variant="outlined"
                                                    />
                                                )}
                                                {campaignFilter !== 'all' && (
                                                    <Chip
                                                        size="small"
                                                        label={`Campaign: ${campaignFilter}`}
                                                        onDelete={() => setCampaignFilter('all')}
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary">
                                                Showing {filteredEntries.length} of {allLedgerEntries.length} transaction(s)
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                )}

                                {/* Ledger Table */}
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell>Wallet ID</TableCell>
                                                <TableCell>Campaign</TableCell>
                                                <TableCell>Shop ID</TableCell>
                                                <TableCell align="right">Credit (DR)</TableCell>
                                                <TableCell align="right">Debit (CR)</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedEntries.map((entry) => {
                                                const drValue = parseFloat(entry.drValue) || 0;
                                                const crValue = parseFloat(entry.crValue) || 0;

                                                return (
                                                    <TableRow key={entry.id} hover>
                                                        <TableCell>
                                                            <Tooltip title={formatDateTime(entry.dateCreated)}>
                                                                <Typography variant="body2">
                                                                    {formatDate(entry.dateCreated)}
                                                                </Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    maxWidth: 250,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {entry.desp || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.7rem' }}>
                                                                {entry.walletID?.slice(0, 12)}...
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {entry.campaign_name || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {entry.shopID || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {drValue > 0 ? (
                                                                <Typography variant="body2" fontWeight={600} color="success.main">
                                                                    {formatAmount(drValue)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body2" color="text.disabled">
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {crValue > 0 ? (
                                                                <Typography variant="body2" fontWeight={600} color="error.main">
                                                                    {formatAmount(crValue)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body2" color="text.disabled">
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={600}
                                                                color={entry.amount > 0 ? 'success.main' : entry.amount < 0 ? 'error.main' : 'text.primary'}
                                                            >
                                                                {formatAmount(entry.amount)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}

                                            {/* Totals Row */}
                                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                                <TableCell colSpan={5}>
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        TOTALS
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="subtitle2" fontWeight={700} color="success.main">
                                                        {formatAmount(summary.totalDr)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="subtitle2" fontWeight={700} color="error.main">
                                                        {formatAmount(summary.totalCr)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="subtitle2" fontWeight={700} color="info.main">
                                                        {formatAmount(summary.totalAmount)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Pagination */}
                                {pageCount > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Pagination
                                            count={pageCount}
                                            page={page}
                                            onChange={(_, value) => setPage(value)}
                                            color="primary"
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Box>
                                )}
                            </>
                        )}

                        {/* Refresh Indicator */}
                        {refreshing && (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    bottom: 16,
                                    right: 16,
                                    zIndex: 1100
                                }}
                            >
                                <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={20} color="inherit" />
                                        <Typography variant="caption">Refreshing...</Typography>
                                    </Stack>
                                </Paper>
                            </Box>
                        )}

                        {/* Snackbar */}
                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={4000}
                            onClose={handleCloseSnackbar}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        >
                            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                                {snackbar.message}
                            </Alert>
                        </Snackbar>

                    </Container>
                </Box>
            </IonContent>
        </IonPage>
    );
};

export default RevenueReport;